import React from "react";
import { Search } from "lucide-react";

import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { cn } from "@workspace/ui/lib/utils";

import type { EposTab } from "../epos-context";

export function MoveOrderToTabDialog({
	isOpen,
	onOpenChange,
	tabs,
	currentTabId,
	onConfirm,
	isMoving,
}: {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	tabs: EposTab[];
	currentTabId: string;
	onConfirm: (toTabId: string) => void;
	isMoving: boolean;
}) {
	const [searchQuery, setSearchQuery] = React.useState("");
	const [selectedTabId, setSelectedTabId] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!isOpen) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- HA-738: react-hooks 7.1 finding, refactor deferred
			setSearchQuery("");
			setSelectedTabId(null);
		}
	}, [isOpen]);

	const eligibleTabs = React.useMemo(() => {
		return tabs.filter((t) => {
			if (t.id === currentTabId) return false;
			if (t.status !== "Open") return false;
			if (searchQuery) {
				const query = searchQuery.toLowerCase();
				return (
					(t.name && t.name.toLowerCase().includes(query)) ||
					t.number.toString().includes(query)
				);
			}
			return true;
		});
	}, [tabs, currentTabId, searchQuery]);

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[520px]">
				<DialogHeader>
					<DialogTitle>Move Order to Tab</DialogTitle>
					<DialogDescription>
						Select a destination tab to move this order.
					</DialogDescription>
				</DialogHeader>

				<div className="relative">
					<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						placeholder="Search by tab name or number..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-9"
					/>
				</div>

				{eligibleTabs.length === 0 ? (
					<div className="py-4 text-center text-sm text-muted-foreground">
						No eligible tabs found.
					</div>
				) : (
					<ScrollArea className="max-h-[340px] pr-2">
						<div className="space-y-2">
							{eligibleTabs.map((tab) => {
								// HA-554: a tab that already holds orders takes this one too (AC7).
								const orderCount = tab.orders.length;
								return (
									<button
										key={tab.id}
										type="button"
										className={cn(
											"w-full rounded-md border px-3 py-3 text-left cursor-pointer hover:bg-muted/50",
											selectedTabId === tab.id && "border-primary",
										)}
										onClick={() => setSelectedTabId(tab.id)}
									>
										<div className="flex items-center justify-between gap-2">
											<div className="truncate font-medium">{tab.name}</div>
											<div className="flex items-center gap-2">
												<Badge variant="outline">#{tab.number}</Badge>
												<Badge variant="outline">
													{tab.covers} cover{tab.covers !== 1 ? "s" : ""}
												</Badge>
												{orderCount > 0 && (
													<Badge variant="secondary">
														{orderCount} order{orderCount !== 1 ? "s" : ""}
													</Badge>
												)}
											</div>
										</div>
									</button>
								);
							})}
						</div>
					</ScrollArea>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						onClick={() => selectedTabId && onConfirm(selectedTabId)}
						disabled={!selectedTabId || isMoving}
					>
						{isMoving ? "Moving..." : "Move Order"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
